import React from 'react';

import { GlobalMessages } from '../../types/Messages';
import {
  LanguageWithMessages,
} from '../../types/common';
import { messageHooks } from '../messageHooks/messageHooks';

export const languageHooks = {
  useGetMessages(
    config: ApplicationSiteGroup,
    locales: string[],
  ): LanguageWithMessages {
    return React.useMemo(() => {
      const langsWithMessages: LanguageWithMessages = {};
      const country = CONFIG.cadeaubonGraphql.country.toString().toLowerCase();
      for (const locale of locales) {
        langsWithMessages[locale] =
          require(`../../messages/${config}/${locale.slice(0, 2)}-${country}.json`);
      }
      return langsWithMessages;
    }, [config, locales]);
  },
  useGetLanguage(
    locale: string | null,
    defaultLocale: string,
    config: ApplicationSiteGroup,
    locales: string[],
  ): LanguageWithMessages | null {
    const langsWithMessages = languageHooks.useGetMessages(config, locales);
    const country = CONFIG.cadeaubonGraphql.country.toString().toLowerCase();

    return React.useMemo(() => {
      if (!(defaultLocale in langsWithMessages)) return null;
      if (!locale || !(`${locale}-${country}` in langsWithMessages)) {
        return {
          [defaultLocale]: langsWithMessages[defaultLocale],
        };
      }
      return {
        [`${locale}-${country}`]: langsWithMessages[`${locale}-${country}`],
      };
    }, [country, defaultLocale, langsWithMessages, locale]);
  },
  useGetLanguageParamRegex() {
    return React.useMemo(() => {
      return CONFIG.locales.map(lang => lang.slice(0, 2)).join('|');
    }, []);
  },
  useGetLocaleAndMessage(currentLanguage: LanguageWithMessages | null) {
    return React.useMemo(() => {
      if (!currentLanguage) {
        return null;
      }
      const [locale, messages] = Object.entries(currentLanguage)[0];
      return {
        locale,
        messages,
      };
    }, [currentLanguage]);
  },
  useSetLanguageInThePage(language: string) {
    const setLanguage = messageHooks.useNext(GlobalMessages.Language);
    React.useEffect(() => {
      setLanguage(
        language === CONFIG.defaultLocale ? '' : language,
      );
    }, [language, setLanguage]);
  },
  useGetLanguageLink(href: string) {
    const language = messageHooks.useSubjectState(GlobalMessages.Language);
    return React.useMemo(() => {
      if (!language || language === CONFIG.defaultLocale.slice(0, 2)) return href;
      if (href.startsWith(`/${language}`)) return href;
      return `/${language}${href}`;
    }, [href, language]);
  },
};
